import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval } from 'rxjs';
import { AlertService } from '@pharvio/core/ui';
import { BUILD_INFO } from '../../../environments/build-info';

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly alertService = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);

  init(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.promptReload());

    // Poll for updates every 30 minutes.
    interval(30 * 60 * 1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.swUpdate.checkForUpdate());

    // Unrecoverable SW state — still user-triggered, not automatic.
    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.promptReload('Pharvios needs to reload to continue.')
      );
  }

  /**
   * Non-blocking info snackbar with a Reload action.
   * Never auto-reloads — a cashier may be mid-sale.
   */
  private promptReload(
    message = `Pharvios ${BUILD_INFO.version} is running. A newer version is available.`
  ): void {
    const ref = this.alertService.info({
      message,
      buttonText: 'Reload',
      duration: 0 // persist until dismissed
    });
    ref?.afterDismissed().subscribe(({ dismissedByAction }) => {
      if (dismissedByAction) {
        document.location.reload();
      }
    });
  }
}
